import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getNumberAnswerFromItem } from 'components/Assessments/helpers'
import { UNANSWERED_DECIMAL_DEFAULT } from 'components/Assessments/PatAssessment/constants'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { ScoreAndSummaryFormField } from './formField'

/**
 * Score and Summary subsection adapter for PAT Assessment
 * Handles score and summary extraction and transformation
 */
export class ScoreAndSummaryAdapter extends BaseSubsectionAdapter<PatFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    // Extract decimal values for calculated score fields
    const modifierA =
      getNumberAnswerFromItem(
        flatAnswers[PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_A]
      ) ?? UNANSWERED_DECIMAL_DEFAULT

    const modifierB =
      getNumberAnswerFromItem(
        flatAnswers[PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_B]
      ) ?? UNANSWERED_DECIMAL_DEFAULT

    const cnaSkilledTasks =
      getNumberAnswerFromItem(
        flatAnswers[PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS]
      ) ?? UNANSWERED_DECIMAL_DEFAULT

    const total =
      getNumberAnswerFromItem(
        flatAnswers[PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_TOTAL]
      ) ?? UNANSWERED_DECIMAL_DEFAULT

    return {
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A]: modifierA,
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B]: modifierB,
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS]: cnaSkilledTasks,
      [ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL]: total
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build decimal value mutations
    if (this.formDataFieldExists(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A)) {
      const value = this.getFormDataField(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_A)
      if (typeof value === 'number' && value !== UNANSWERED_DECIMAL_DEFAULT) {
        mutations.push({
          linkId: PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_A,
          value
        })
      }
    }

    if (this.formDataFieldExists(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B)) {
      const value = this.getFormDataField(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_MODIFIER_B)
      if (typeof value === 'number' && value !== UNANSWERED_DECIMAL_DEFAULT) {
        mutations.push({
          linkId: PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_MODIFIER_B,
          value
        })
      }
    }

    if (this.formDataFieldExists(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS)) {
      const value = this.getFormDataField(
        ScoreAndSummaryFormField.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS
      )
      if (typeof value === 'number' && value !== UNANSWERED_DECIMAL_DEFAULT) {
        mutations.push({
          linkId: PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_CNA_SKILLED_TASKS,
          value
        })
      }
    }

    if (this.formDataFieldExists(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL)) {
      const value = this.getFormDataField(ScoreAndSummaryFormField.SCORE_AND_SUMMARY_TOTAL)
      if (typeof value === 'number' && value !== UNANSWERED_DECIMAL_DEFAULT) {
        mutations.push({
          linkId: PatAssessmentQuestionnaireItem.SCORE_AND_SUMMARY_TOTAL,
          value
        })
      }
    }

    return mutations
  }
}
